import { RadioButton, Typography } from '@weareopenr/component-library';
import { useAuthWrapper } from '@weareopenr/authkit';
import classnames from 'classnames/bind';
import { Control, Controller, FieldValues, UseFormRegister } from 'react-hook-form';
import { IRange } from 'src/api/getRanges';
import { FEATURE_FLAGS } from 'src/constants';
import { RangesField } from './RangesField';
import styles from './MenuSourceField.module.scss';

const cx = classnames.bind(styles);

interface MenuSourceFieldProps<T extends FieldValues & { rangeIds: string[] }> {
  register: UseFormRegister<any>;
  control: Control<T>;
  isRangesSource: boolean;
  rangesEnabled: boolean;
  ranges: IRange[];
  isRangesLoading: boolean;
}

export const MenuSourceField = <T extends FieldValues & { rangeIds: string[] }>({
  register,
  control,
  isRangesSource,
  rangesEnabled,
  ranges,
  isRangesLoading,
}: MenuSourceFieldProps<T>) => {
  const { featureFlags } = useAuthWrapper();
  // Timetables only offer menu sets to brands with the feature (see useTimetableMenuSets).
  const allLabel = featureFlags.includes(FEATURE_FLAGS.MENU_SETS) ? 'All Menus and Menu Sets' : 'All Menus';

  return (
    <div className={cx('container')}>
      <Typography size="medium" weight="bold">
        Menu Source:
      </Typography>
      <div className={cx('radios')}>
        <RadioButton name="menuSource" value="all" register={register}>
          <Typography weight="bold" size="medium">
            {allLabel}
          </Typography>
        </RadioButton>
        {rangesEnabled && (
          <RadioButton name="menuSource" value="ranges" register={register}>
            <Typography weight="bold" size="medium">
              Use defined Ranges
            </Typography>
          </RadioButton>
        )}
        {isRangesSource && (
          <Controller
            // react-hook-form can't prove a generic T satisfies Path<T> from an intersection
            // constraint alone - narrow to the one field this component actually touches.
            control={control as unknown as Control<{ rangeIds: string[] }>}
            name="rangeIds"
            rules={{ validate: (value) => value.length > 0 || 'Please select at least one range' }}
            render={({ field, fieldState: { error } }) => (
              <RangesField
                ranges={ranges}
                isLoading={isRangesLoading}
                value={field.value}
                onChange={field.onChange}
                error={error?.message}
              />
            )}
          />
        )}
      </div>
    </div>
  );
};

export default MenuSourceField;
